<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="editDriver">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Driver</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>

                    <v-row>
                        <v-col v-if="user.type === 'admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies" :rules="[vRules.required]"
                                      density="compact" label="Company"
                                      @update:modelValue="()=>{formData['device_id']=null;fetchDevices();}">
                            </company-select>
                        </v-col>

                        <!-- Photo upload -->
                        <v-col cols="12" class="py-0 d-flex align-center mb-2">
                            <v-avatar size="64" color="grey-lighten-3" class="mr-4" style="cursor: pointer;" @click="$refs.photoInput?.click()">
                                <v-img v-if="avatarPreviewUrl" :src="avatarPreviewUrl" cover/>
                                <v-icon v-else size="32" color="grey">mdi-camera</v-icon>
                            </v-avatar>
                            <div>
                                <v-btn size="small" variant="outlined" prepend-icon="mdi-upload" @click="$refs.photoInput?.click()">
                                    Change Photo
                                </v-btn>
                                <v-btn v-if="formData['image']" size="small" variant="text" color="error" icon="mdi-close" class="ml-1"
                                       @click="formData['image'] = null; avatarPreviewUrl = existingAvatar"/>
                                <input ref="photoInput" type="file" accept="image/jpeg,image/png,image/jpg" hidden
                                       @change="onPhotoSelected"/>
                                <div class="text-caption text-grey mt-1">JPEG or PNG, max 2MB</div>
                            </div>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="4">
                            <v-text-field v-model="formData['first_name']" :rules="[vRules.required]"
                                          density="compact" label="First Name *"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="4">
                            <v-text-field v-model="formData['middle_name']"
                                          density="compact" label="Middle Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="4">
                            <v-text-field v-model="formData['last_name']"
                                          density="compact" label="Last Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['email']" :rules="[vRules.required, vRules.email]"
                                          density="compact" label="Email *" type="email"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['phone']"
                                          density="compact" label="Phone"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['mobile_phone']"
                                          density="compact" label="Mobile Phone"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-autocomplete v-model="formData['timezone']" :items="timezones"
                                            density="compact" label="Timezone" clearable/>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['department']" density="compact" label="Department"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['rfid']" density="compact" label="RFID"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['device_id']" :items="devices" clearable
                                      density="compact" item-title="slno" item-value="id" label="Device">
                            </v-select>
                        </v-col>

                        <v-col cols="12" class="py-0">
                            <v-expansion-panels variant="accordion" class="mb-2">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-body-2 py-2">
                                        <v-icon size="18" class="mr-2">mdi-card-account-details</v-icon>
                                        License Information
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <v-row>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['license_number']" density="compact"
                                                              label="License Number" placeholder="DL-123456"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['license_state']" density="compact"
                                                              label="Issuing State"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-select v-model="formData['license_class']" density="compact"
                                                          label="License Class" :items="licenseClasses" clearable/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['license_expiry']" density="compact"
                                                              label="Expiry Date" type="date"/>
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>

                        <v-col cols="12" class="py-0">
                            <v-expansion-panels variant="accordion" class="mb-2">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-body-2 py-2">
                                        <v-icon size="18" class="mr-2">mdi-map-marker-outline</v-icon>
                                        Address
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <v-row>
                                            <v-col cols="12" class="py-0">
                                                <v-text-field v-model="formData['addr_1']" density="compact" label="Address Line 1"/>
                                            </v-col>
                                            <v-col cols="12" class="py-0">
                                                <v-text-field v-model="formData['addr_2']" density="compact" label="Address Line 2"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['addr_city']" density="compact" label="City"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['addr_state']" density="compact" label="State"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['addr_postalcode']" density="compact" label="Postal Code"/>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="py-0">
                                                <v-text-field v-model="formData['addr_country']" density="compact" label="Country"/>
                                            </v-col>
                                        </v-row>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-col>

                        <v-col cols="12" class="py-0">
                            <v-textarea v-model="formData['notes']" density="compact" label="Notes" rows="2"
                                        auto-grow placeholder="Any additional notes about this driver..."/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['current_status']"
                                      :items="[{id:0,title:'Idle'},{id:1,title:'Driving'}]"
                                      :rules="[vRules.required]" density="compact" item-title="title" item-value="id"
                                      label="Current Status">
                            </v-select>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['status']"
                                      :items="[{id:0,title:'Disabled'},{id:1,title:'Enabled'}]"
                                      :rules="[vRules.required]" density="compact" item-title="title" item-value="id"
                                      label="Status">
                            </v-select>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="loading" :loading="loading" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import timezones from "@/assets/js/timezones.json";
import CompanySelect from "@/components/globals/company-select.vue";

const formReady = ref(false);
const loading = ref(false);
const {user} = useAuthStore();
const devices = ref([]);
const companies = ref([]);
const snackbar = useSnackbarStore();
const photoInput = ref(null);
const props = defineProps({
    modelValue: Boolean,
    driver: {
        type: Object
    }
});

const existingAvatar = props.driver.user_avatar || null;
const avatarPreviewUrl = ref(existingAvatar);

const formData = reactive({
    valid: false,
    _method: 'put',
    company_id: props.driver.company_id,
    first_name: props.driver.user_first_name || props.driver.first_name || null,
    middle_name: props.driver.user?.middle_name || null,
    last_name: props.driver.user_last_name || props.driver.last_name || null,
    email: props.driver.user_email || props.driver.email || null,
    phone: props.driver.user_phone || props.driver.phone || null,
    mobile_phone: props.driver.user_mobile_phone || props.driver.user?.mobile_phone || null,
    timezone: props.driver.user_timezone || props.driver.user?.timezone || null,
    image: null,
    department: props.driver.department,
    rfid: props.driver.rfid,
    device_id: props.driver.device_id,
    status: props.driver.status ? 1 : 0,
    current_status: props.driver.current_status ? 1 : 0,
    license_number: props.driver.license_number ?? null,
    license_state: props.driver.license_state ?? null,
    license_class: props.driver.license_class ?? null,
    license_expiry: props.driver.license_expiry ?? null,
    notes: props.driver.notes ?? null,
    addr_1: props.driver.user?.addr_1 || null,
    addr_2: props.driver.user?.addr_2 || null,
    addr_city: props.driver.user?.addr_city || null,
    addr_state: props.driver.user?.addr_state || null,
    addr_postalcode: props.driver.user?.addr_postalcode || null,
    addr_country: props.driver.user?.addr_country || null,
});
const licenseClasses = ['A', 'B', 'C', 'D', 'CDL-A', 'CDL-B', 'CDL-C', 'Non-CDL'];
const emits = defineEmits(['update:modelValue', 'updateDriver']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);

onMounted(() => {
    const promises = [fetchDevices()];
    if (user.type === 'admin') {
        promises.push(fetchCompaniesList());
    }
    Promise.all(promises).finally(() => formReady.value = true);
})
const fetchCompaniesList = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchDevices = () => {
    return send('post', `drivers/available-device-list?device=${formData['device_id']}${(user.type === 'admin' ? '&company=' + formData['company_id'] : '')}`)
        .then((response) => {
            devices.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Devices could not be loaded', 'error')
        })
}

const onPhotoSelected = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
        snackbar.showSnackbar('Image must be less than 2MB', 'error');
        return;
    }
    formData['image'] = file;
    avatarPreviewUrl.value = URL.createObjectURL(file);
};

const editDriver = () => {
    errors.value = [];
    loading.value = true;
    const fd = new FormData();
    Object.keys(formData).forEach(key => {
        if (key === 'valid') return;
        if (formData[key] !== null && formData[key] !== undefined) {
            fd.append(key, formData[key]);
        }
    });
    send('post', `companies/drivers/${props.driver.id}`, fd, {
        headers: {'Content-Type': 'multipart/form-data'}
    })
        .then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            const driver = response.data.data;
            if (driver.user) {
                driver.user_first_name = driver.user.first_name;
                driver.user_last_name = driver.user.last_name;
                driver.user_email = driver.user.email;
                driver.user_phone = driver.user.contact_phone;
                driver.user_mobile_phone = driver.user.mobile_phone;
                driver.user_timezone = driver.user.timezone;
                driver.user_avatar = driver.user.avatar;
            }
            emits('updateDriver', driver);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response.data.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    }).finally(() => loading.value = false)
}

</script>
